import React from 'react';
import { FlaskConical, ChevronDown, ChevronUp, Edit, Trash2, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { formatCurrency } from '../../lib/utils';

const ANIMAL_TYPES = [
  { value: 'pollo', label: 'Pollo' },
  { value: 'gallina', label: 'Gallina' },
  { value: 'cerdo', label: 'Cerdo' },
  { value: 'becerro', label: 'Becerro' },
];

// Colores para badges de animales
const ANIMAL_COLORS = {
  'pollo': 'bg-red-100 text-red-800',
  'gallina': 'bg-green-100 text-green-800',
  'cerdo': 'bg-pink-100 text-pink-800',
  'becerro': 'bg-amber-100 text-amber-800'
};

export function FormulasGrid({ 
  formulas, 
  expandedFormula, 
  setExpandedFormula, 
  isSuperUser, 
  onEdit, 
  onDelete 
}) {
  if (formulas.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-8 text-center text-slate-500">
          No hay fórmulas registradas. Crea una nueva fórmula para comenzar.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {formulas.map((formula) => (
        <Card key={formula.id} className="border-slate-200">
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => setExpandedFormula(expandedFormula === formula.id ? null : formula.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#E97171]/20 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-[#931A25]" />
                </div>
                <div>
                  <CardTitle className="text-lg">{formula.name}</CardTitle>
                  <p className="text-sm text-slate-500">
                    {formula.type}
                    {formula.animal_type && (
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${ANIMAL_COLORS[formula.animal_type] || 'bg-blue-100 text-blue-700'}`}>
                        {ANIMAL_TYPES.find(a => a.value === formula.animal_type)?.label || formula.animal_type}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Costo Materiales</p>
                  <p className="font-semibold text-[#931A25]">US$ {formula.estimated_cost_usd?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-slate-400">{formatCurrency(formula.estimated_cost)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Costo/{formula.output_unit}</p>
                  <p className="font-semibold text-emerald-600">US$ {formula.unit_cost_usd?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-slate-400">{formatCurrency(formula.unit_cost || 0)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Produce</p>
                  <p className="font-semibold">{formula.output_quantity} {formula.output_unit}</p>
                </div>
                {expandedFormula === formula.id ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
              </div>
            </div>
          </CardHeader>
          
          {expandedFormula === formula.id && (
            <CardContent className="border-t">
              <div className="pt-4">
                <h4 className="font-semibold text-slate-900 mb-3">Ingredientes</h4>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2">Material</th>
                      <th className="text-right p-2">Cantidad</th>
                      <th className="text-center p-2">Unidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formula.ingredients.map((ing, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{ing.material_name}</td>
                        <td className="p-2 text-right">{ing.quantity}</td>
                        <td className="p-2 text-center">{ing.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={(e) => { e.stopPropagation(); onEdit(formula); }}
                    className={`border-blue-200 hover:bg-blue-50 ${isSuperUser ? 'text-blue-600' : 'text-slate-400'}`}
                    title={isSuperUser ? 'Editar' : 'Requiere Super Usuario'}
                  >
                    <Edit className="h-4 w-4 mr-2" />Editar
                    {!isSuperUser && <Shield className="h-3 w-3 ml-1 text-amber-500" />}
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={(e) => { e.stopPropagation(); onDelete(formula); }}
                    className={`border-red-200 hover:bg-red-50 ${isSuperUser ? 'text-red-600' : 'text-slate-400'}`}
                    title={isSuperUser ? 'Eliminar' : 'Requiere Super Usuario'}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />Eliminar
                    {!isSuperUser && <Shield className="h-3 w-3 ml-1 text-amber-500" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}
